// Only the Dashboard is shown on first paint, so it stays eagerly imported.
// Every other view is code-split via React.lazy so its JS (and heavy deps like
// the colyseus game clients, admin tables, legal prose) leaves the landing
// chunk and is fetched on demand. A single <Suspense> boundary lives around
// <Router/> in App.tsx. Behaviour is unchanged — only load timing differs.
import { lazy } from "react";
import Dashboard from "../pages/Dashboard";

const DocsPage = lazy(() => import("../pages/DocsPage"));
const FPSBriefingScreen = lazy(() => import("../pages/FPSBriefingScreen"));
const FPSGameEmbed = lazy(() => import("../pages/FPSGameEmbed"));
const FPSStakeScreen = lazy(() => import("../pages/FPSStakeScreen"));
const LeaderboardPage = lazy(() => import("../pages/LeaderboardPage"));
const MathGame = lazy(() => import("../pages/MathGame"));
const MathStakeScreen = lazy(() => import("../pages/MathStakeScreen"));
const PongGame = lazy(() => import("../pages/PongGame"));
const ProfilePage = lazy(() => import("../pages/ProfilePage"));
const ProfileSettingsPage = lazy(() => import("../pages/ProfileSettingsPage"));
const TournamentsPage = lazy(() => import("../pages/TournamentsPage"));
const SnakeGame = lazy(() => import("../pages/SnakeGame"));
const SnakeStakeScreen = lazy(() => import("../pages/SnakeStakeScreen"));
const StatsPage = lazy(() => import("../pages/StatsPage"));
const TowerDefenseGame = lazy(() => import("../pages/TowerDefenseGame"));
const TowerDefenseStakeScreen = lazy(() => import("../pages/TowerDefenseStakeScreen"));
const StatusPage = lazy(() => import("../components/StatusPage"));
const AdminDashboard = lazy(() => import("../pages/AdminDashboard"));
const LegalCenterPage = lazy(() => import("../pages/legal/LegalCenterPage"));
const TermsOfServicePage = lazy(() => import("../pages/legal/TermsOfServicePage"));
const PrivacyPolicyPage = lazy(() => import("../pages/legal/PrivacyPolicyPage"));
const ResponsibleGamingPage = lazy(() => import("../pages/legal/ResponsibleGamingPage"));
const RestrictedRegionsPage = lazy(() => import("../pages/legal/RestrictedRegionsPage"));
import type { LegalView } from "../pages/legal/legalPages";

export type AppView =
  | "dashboard"
  | "docs"
  | "pong"
  | "fps"
  | "fps-stake"
  | "fps-briefing"
  | "td-stake"
  | "tower-defense"
  | "snake-stake"
  | "snake"
  | "math-stake"
  | "math"
  | "leaderboard"
  | "tournaments"
  | "tournament-math"
  | "tournament-td"
  | "tournament-fps"
  | "tournament-pong"
  | "custom-fps"
  | "custom-pong"
  | "custom-math"
  | "custom-td"
  | "profile"
  | "stats"
  | "status"
  | "settings"
  | "admin"
  | "legal"
  | "terms"
  | "privacy"
  | "responsible-gaming"
  | "restricted-regions";

/** A bracket match the logged-in user is being routed into. */
export interface TournamentMatchContext {
  tournamentId: string;
  matchId: string;
  game: "fps" | "pong" | "math" | "td";
  /** Nominal stake (the entry) — shown in-game; no per-match charge. */
  stake: number;
  /** `<tournamentId>:<matchId>` — pairs the two opponents in the room. */
  tournamentMatchId: string;
}

export interface SnakeEntry {
  stake: number;
  skinId: string;
  handle: string;
}

/** A private friend-vs-friend custom match the user is being routed into. */
export interface CustomMatchContext {
  game: "fps" | "pong" | "math" | "td";
  /** Integer USD stake per player (both sides locked into escrow). */
  stake: number;
  /** `cm:<inviteId>` — pairs exactly the two friends into one room. */
  customMatchId: string;
  /** The opponent's wallet (for the VS splash / display). */
  opponentWallet: string;
}

interface RouterProps {
  currentView: AppView;
  onPlayGame: (game: AppView, wager?: number) => void;
  onOpenTournaments: () => void;
  onShowWallet: () => void;
  onCancelStake: () => void;
  onBackFromBriefing: () => void;
  onFindMatch: (amount: number) => void;
  onFindTDMatch: (amount: number) => void;
  onEnterSnakeServer: (entry: SnakeEntry) => void;
  onFindMathMatch: (amount: number) => void;
  onEnterMatch: () => void;
  onBackToDashboard: () => void;
  /** Navigate to a legal document (footer links + in-page cross-links). */
  onOpenLegal: (view: LegalView) => void;
  stake?: number;
  snakeEntry?: SnakeEntry;
  walletAddress: string | null;
  /** Hupper JWT (from useAuth) — verified by the Colyseus room's onAuth so
   *  the client can't claim someone else's wallet. Forwarded to the game
   *  pages so each one can pass it into its `joinXRoom` call. */
  apiToken: string | null;
  /** Reconnection token from the FPS reconnect card — relaunches the FPS
   *  iframe straight into the saved seat instead of a fresh match. */
  fpsReconnectToken?: string | null;
  userHandle?: string | null;
  /** When set, the profile view renders this user's stats instead of the
   *  logged-in user's. Used by the friends panel to surface another
   *  player's profile. */
  viewedProfile?: { address: string; handle: string } | null;
  /** Lets the profile view clear the viewedProfile (i.e. "back to mine"). */
  onClearViewedProfile?: () => void;
  /** Settings page → "view my public profile". */
  onViewProfile?: () => void;
  /** Leaderboard → open a clicked player's public profile. */
  onViewPlayerProfile?: (address: string, handle: string) => void;
  /** Tournament detail → route the user into their ready bracket match. */
  onEnterTournamentMatch?: (ctx: TournamentMatchContext) => void;
  /** Game page → back to the tournament detail once the match resolves. */
  onTournamentMatchEnd?: () => void;
  /** The active tournament match (set while in tournament-math / -td). */
  tournamentMatch?: TournamentMatchContext | null;
  /** When returning from a match, reopen this tournament's detail. */
  returnTournamentId?: string | null;
  /** The active custom match (set while in a custom-* view). */
  customMatch?: CustomMatchContext | null;
  /** Game page → back to the dashboard once the custom match resolves. */
  onCustomMatchEnd?: () => void;
  /** Open the challenge modal for a friend (from the profile page). */
  onChallenge?: (target: { address: string; handle: string; avatarVersion: number }) => void;
}

export default function Router({
  currentView,
  onPlayGame,
  onOpenTournaments,
  onShowWallet,
  onCancelStake,
  onBackFromBriefing,
  onFindMatch,
  onFindTDMatch,
  onEnterSnakeServer,
  onFindMathMatch,
  onEnterMatch,
  onBackToDashboard,
  onOpenLegal,
  stake,
  snakeEntry,
  walletAddress,
  apiToken,
  fpsReconnectToken,
  userHandle,
  viewedProfile,
  onClearViewedProfile,
  onViewProfile,
  onViewPlayerProfile,
  onEnterTournamentMatch,
  onTournamentMatchEnd,
  tournamentMatch,
  returnTournamentId,
  customMatch,
  onCustomMatchEnd,
  onChallenge,
}: RouterProps) {
  if (currentView === "dashboard") {
    return <Dashboard onPlayGame={onPlayGame} onOpenTournaments={onOpenTournaments} />;
  }

  if (currentView === "docs") {
    return <DocsPage onBack={onBackToDashboard} onPlayNow={onBackToDashboard} />;
  }

  if (currentView === "tournaments") {
    return (
      <TournamentsPage
        apiToken={apiToken}
        walletAddress={walletAddress}
        initialTournamentId={returnTournamentId ?? null}
        onEnterTournamentMatch={onEnterTournamentMatch}
      />
    );
  }

  if (currentView === "tournament-math" && tournamentMatch) {
    return (
      <MathGame
        stake={tournamentMatch.stake}
        walletAddress={walletAddress}
        apiToken={apiToken}
        tournamentMatchId={tournamentMatch.tournamentMatchId}
        onExit={() => onTournamentMatchEnd?.()}
      />
    );
  }

  if (currentView === "tournament-td" && tournamentMatch) {
    return (
      <TowerDefenseGame
        stake={tournamentMatch.stake}
        walletAddress={walletAddress}
        apiToken={apiToken}
        tournamentMatchId={tournamentMatch.tournamentMatchId}
        onExit={() => onTournamentMatchEnd?.()}
      />
    );
  }

  if (currentView === "tournament-fps" && tournamentMatch) {
    return (
      <FPSGameEmbed
        walletAddress={walletAddress}
        apiToken={apiToken}
        stake={tournamentMatch.stake}
        tournamentMatchId={tournamentMatch.tournamentMatchId}
        onShowWallet={onShowWallet}
        onExit={() => onTournamentMatchEnd?.()}
      />
    );
  }

  if (currentView === "tournament-pong" && tournamentMatch) {
    return (
      <PongGame
        walletAddress={walletAddress}
        apiToken={apiToken}
        onShowWallet={onShowWallet}
        tournamentStake={tournamentMatch.stake}
        tournamentMatchId={tournamentMatch.tournamentMatchId}
        onTournamentMatchExit={() => onTournamentMatchEnd?.()}
      />
    );
  }

  // ── Private friend-vs-friend custom matches ──────────────────────────
  if (currentView === "custom-math" && customMatch) {
    return (
      <MathGame
        stake={customMatch.stake}
        walletAddress={walletAddress}
        apiToken={apiToken}
        customMatchId={customMatch.customMatchId}
        onExit={() => onCustomMatchEnd?.()}
      />
    );
  }

  if (currentView === "custom-td" && customMatch) {
    return (
      <TowerDefenseGame
        stake={customMatch.stake}
        walletAddress={walletAddress}
        apiToken={apiToken}
        customMatchId={customMatch.customMatchId}
        onExit={() => onCustomMatchEnd?.()}
      />
    );
  }

  if (currentView === "custom-pong" && customMatch) {
    return (
      <PongGame
        walletAddress={walletAddress}
        apiToken={apiToken}
        onShowWallet={onShowWallet}
        customStake={customMatch.stake}
        customMatchId={customMatch.customMatchId}
        onCustomMatchExit={() => onCustomMatchEnd?.()}
      />
    );
  }

  if (currentView === "custom-fps" && customMatch) {
    return (
      <FPSGameEmbed
        walletAddress={walletAddress}
        apiToken={apiToken}
        stake={customMatch.stake}
        customMatchId={customMatch.customMatchId}
        onShowWallet={onShowWallet}
        onExit={() => onCustomMatchEnd?.()}
      />
    );
  }

  if (currentView === "pong") {
    return <PongGame walletAddress={walletAddress} apiToken={apiToken} onShowWallet={onShowWallet} />;
  }

  if (currentView === "fps-stake") {
    return (
      <FPSStakeScreen
        walletAddress={walletAddress}
        onCancel={onCancelStake}
        onFindMatch={onFindMatch}
        onShowWallet={onShowWallet}
      />
    );
  }

  if (currentView === "fps-briefing") {
    return (
      <FPSBriefingScreen
        stake={stake ?? 10}
        onBack={onBackFromBriefing}
        onEnterMatch={onEnterMatch}
      />
    );
  }

  if (currentView === "fps") {
    return (
      <FPSGameEmbed
        walletAddress={walletAddress}
        apiToken={apiToken}
        stake={stake}
        onShowWallet={onShowWallet}
        onExit={onBackToDashboard}
        reconnectToken={fpsReconnectToken}
      />
    );
  }

  if (currentView === "td-stake") {
    return (
      <TowerDefenseStakeScreen
        walletAddress={walletAddress}
        onCancel={onCancelStake}
        onFindMatch={onFindTDMatch}
        onShowWallet={onShowWallet}
      />
    );
  }

  if (currentView === "tower-defense") {
    return (
      <TowerDefenseGame
        stake={stake ?? 10}
        walletAddress={walletAddress}
        apiToken={apiToken}
        onExit={onBackToDashboard}
      />
    );
  }

  if (currentView === "math-stake") {
    return (
      <MathStakeScreen
        onCancel={onCancelStake}
        onFindMatch={onFindMathMatch}
        walletAddress={walletAddress}
        onShowWallet={onShowWallet}
      />
    );
  }

  if (currentView === "math") {
    return (
      <MathGame
        stake={stake ?? 5}
        walletAddress={walletAddress}
        apiToken={apiToken}
        onExit={onBackToDashboard}
      />
    );
  }

  if (currentView === "snake-stake") {
    return (
      <SnakeStakeScreen
        walletAddress={walletAddress}
        defaultHandle={userHandle}
        onCancel={onCancelStake}
        onEnter={onEnterSnakeServer}
        onShowWallet={onShowWallet}
      />
    );
  }

  if (currentView === "snake") {
    return (
      <SnakeGame
        stake={snakeEntry?.stake ?? 1}
        skinId={snakeEntry?.skinId ?? "inferno"}
        handle={snakeEntry?.handle ?? "you"}
        walletAddress={walletAddress}
        apiToken={apiToken}
        onExit={() => onBackToDashboard()}
      />
    );
  }

  if (currentView === "leaderboard") {
    return <LeaderboardPage onViewProfile={onViewPlayerProfile} />;
  }

  if (currentView === "stats") {
    return (
      <StatsPage walletAddress={walletAddress} apiToken={apiToken} onPlayGame={onPlayGame} />
    );
  }

  if (currentView === "status") {
    return <StatusPage onBack={onBackToDashboard} />;
  }

  if (currentView === "admin") {
    return <AdminDashboard apiToken={apiToken} onBack={onBackToDashboard} />;
  }

  if (currentView === "profile") {
    return (
      <ProfilePage
        walletAddress={walletAddress}
        handle={userHandle ?? null}
        apiToken={apiToken}
        viewedAddress={viewedProfile?.address ?? null}
        viewedHandle={viewedProfile?.handle ?? null}
        onBackToOwn={onClearViewedProfile}
        onChallenge={onChallenge}
      />
    );
  }

  if (currentView === "settings") {
    return (
      <ProfileSettingsPage
        walletAddress={walletAddress}
        apiToken={apiToken}
        onBack={onBackToDashboard}
        onViewProfile={onViewProfile}
      />
    );
  }

  if (currentView === "legal") {
    return <LegalCenterPage onBack={onBackToDashboard} onNavigate={onOpenLegal} />;
  }

  if (currentView === "terms") {
    return <TermsOfServicePage onBack={onBackToDashboard} onNavigate={onOpenLegal} />;
  }

  if (currentView === "privacy") {
    return <PrivacyPolicyPage onBack={onBackToDashboard} onNavigate={onOpenLegal} />;
  }

  if (currentView === "responsible-gaming") {
    return <ResponsibleGamingPage onBack={onBackToDashboard} onNavigate={onOpenLegal} />;
  }

  if (currentView === "restricted-regions") {
    return <RestrictedRegionsPage onBack={onBackToDashboard} onNavigate={onOpenLegal} />;
  }

  return null;
}
